import { Descriptions, Empty, Skeleton, Space } from 'antd';
import { constants, DetailKey, Investment, Investor } from 'procura-shared';
import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import calls from '../api/calls';
import { sortKeys } from '../utilities/Utilities';
import { DetailsAddButton, Editable } from './Components';

type DetailsTableProps = {
  keys: DetailKey[];
  details: Investment | Investor;
  adminMode?: boolean;
  loading?: boolean;
};

export const DetailsTable = ({ details, keys, adminMode = false, loading = false }: DetailsTableProps) => {
  const { t } = useTranslation();

  const detailKeys = useMemo(() => Object.keys(details ?? {}) as DetailKey[], [details]);

  const isDate = useCallback((detailKey: DetailKey): boolean => constants.DATE_KEYS.includes(detailKey), []);
  const isCurrency = useCallback((detailKey: DetailKey): boolean => constants.CURRENCY_KEYS.includes(detailKey), []);
  const isPercent = useCallback((detailKey: DetailKey): boolean => constants.PERCENT_KEYS.includes(detailKey), []);
  const isFormatedNumber = useCallback(
    (detailKey: DetailKey): boolean => constants.FORMAT_NUMBER_KEYS.includes(detailKey),
    []
  );

  const noData = (): boolean =>
    details == null ||
    detailKeys.filter((key) => {
      return key !== 'id' && keys.includes(key as keyof typeof details);
    }).length === 0;

  const emptyDetailKeys = useMemo((): DetailKey[] => {
    return keys.filter((key) => !(detailKeys ?? []).includes(key) || details[key] === '');
  }, [detailKeys, details, keys]);

  const itemValue = useCallback(
    (detailKey: DetailKey): any => {
      const value = details[detailKey];
      if (isDate(detailKey)) {
        return new Date(value).toLocaleDateString();
      }
      return value;
    },
    [details, isDate]
  );

  const itemRender = useCallback(
    (detailKey: DetailKey): JSX.Element | undefined => {
      const value = details[detailKey];
      if (isCurrency(detailKey)) {
        const label = new Intl.NumberFormat('da', {
          maximumFractionDigits: 0,
        }).format(value);
        return <span>£{label}</span>;
      }
      if (isPercent(detailKey)) {
        const label = Number(value / 100).toLocaleString('da', { style: 'percent', minimumFractionDigits: 2 });
        return <>{label}</>;
      }
      if (isFormatedNumber(detailKey)) {
        const label = new Intl.NumberFormat('da', {
          maximumFractionDigits: 2,
        }).format(value);
        return <>{label}</>;
      }
      return undefined;
    },
    [details, isCurrency, isFormatedNumber, isPercent]
  );

  const itemAddHandler = (item: { key: DetailKey; value: any }) => {
    itemSubmitHandler(item.key, item.value);
  };

  const itemSubmitHandler = async (detailKey: DetailKey, value: string | number) => {
    details[detailKey] = value;
    await calls.updateDetails(details);
  };

  const renderEditingItem = (detailKey: DetailKey) => (
    <Editable
      value={itemValue(detailKey)}
      detailKey={detailKey}
      editMode={adminMode}
      onSubmit={(value) => itemSubmitHandler(detailKey, value)}
      onDelete={() => itemSubmitHandler(detailKey, '')}
      render={itemRender(detailKey)}
    />
  );

  const renderItems = () =>
    sortKeys(keys)
      .filter((key) => detailKeys.includes(key) && details[key] !== '')
      .map((key, index) => (
        <Descriptions.Item label={t(key)} key={index}>
          {adminMode ? renderEditingItem(key) : itemValue(key)}
        </Descriptions.Item>
      ));

  const renderLoading = () =>
    [...Array(5)].map((_, index) => (
      <Descriptions.Item label={<Skeleton paragraph={{ rows: 1 }} title={false} />} key={index}>
        <Skeleton active paragraph={{ rows: 1 }} title={false} />
      </Descriptions.Item>
    ));

  return (
    <Space direction="vertical" style={{ width: '100%' }}>
      {!loading && noData() ? (
        <Empty />
      ) : (
        <Descriptions bordered column={1} labelStyle={{ width: 300 }}>
          {loading ? renderLoading() : renderItems()}
        </Descriptions>
      )}
      {adminMode && emptyDetailKeys.length > 0 && (
        <DetailsAddButton
          label={t('addDetail')}
          detailKeys={emptyDetailKeys}
          selectPlaceholder={t('detailType')}
          placeholder={t('detailValue')}
          onConfirm={itemAddHandler}
        />
      )}
    </Space>
  );
};
